import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { JurisdictionCode, DEFAULT_JURISDICTION } from '../../common/jurisdictions';
import { RootState } from '../store';

interface DocumentSearchState {
  jurisdiction: JurisdictionCode;
  query: string;
  selectedKnowledgebaseIds: string[];
  searchTerm: string;
  isSubmitting: boolean;
  // Filters and sorting
  relevanceFilter: string[];
  contentTypeFilter: string[];
  confidenceRange: [number, number];
  knowledgebaseFilter: string[];
  scoreThreshold: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

const initialState: DocumentSearchState = {
  jurisdiction: DEFAULT_JURISDICTION,
  query: '',
  selectedKnowledgebaseIds: [],
  searchTerm: '',
  isSubmitting: false,
  // Filters and sorting - default to showing all
  relevanceFilter: [],
  contentTypeFilter: [],
  confidenceRange: [0, 1],
  knowledgebaseFilter: [],
  scoreThreshold: 0,
  sortBy: 'relevance', // Default sort by relevance
  sortDirection: 'desc'
};

const documentSearchSlice = createSlice({
  name: 'documentSearch',
  initialState,
  reducers: {
    setJurisdiction: (state, action: PayloadAction<JurisdictionCode>) => {
      if (state.jurisdiction === action.payload) {
        return;
      }
      state.jurisdiction = action.payload;
      state.selectedKnowledgebaseIds = []; // Reset KBs when jurisdiction changes
    },
    setQuery: (state, action: PayloadAction<string>) => {
      if (state.query === action.payload) {
        return;
      }
      state.query = action.payload;
    },
    setSelectedKnowledgebases: (state, action: PayloadAction<string[]>) => {
      // Check if arrays are the same
      if (JSON.stringify(state.selectedKnowledgebaseIds) === JSON.stringify(action.payload)) {
        return;
      }
      state.selectedKnowledgebaseIds = action.payload;
    },
    toggleKnowledgebase: (state, action: PayloadAction<string>) => {
      const kbId = action.payload;
      const isCurrentlySelected = state.selectedKnowledgebaseIds.includes(kbId);
      
      if (isCurrentlySelected) {
        state.selectedKnowledgebaseIds = state.selectedKnowledgebaseIds.filter(id => id !== kbId);
      } else {
        state.selectedKnowledgebaseIds.push(kbId);
      }
    },
    selectAllKnowledgebases: (state, action: PayloadAction<string[]>) => {
      // Union: add the tab's IDs to whatever is already selected across both tabs
      const existing = new Set(state.selectedKnowledgebaseIds);
      const toAdd = action.payload.filter(id => !existing.has(id));
      if (toAdd.length === 0) return;
      state.selectedKnowledgebaseIds = [...state.selectedKnowledgebaseIds, ...toAdd];
    },
    selectNoKnowledgebases: (state, action: PayloadAction<string[] | undefined>) => {
      if (action.payload?.length) {
        // Remove only the active tab's IDs, preserving the other tab's selections
        const toRemove = new Set(action.payload);
        state.selectedKnowledgebaseIds = state.selectedKnowledgebaseIds.filter(
          id => !toRemove.has(id)
        );
      } else {
        state.selectedKnowledgebaseIds = [];
      }
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      if (state.searchTerm === action.payload) {
        return;
      }
      state.searchTerm = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      if (state.isSubmitting === action.payload) {
        return;
      }
      state.isSubmitting = action.payload;
    },
    resetDocumentSearch: (state, action: PayloadAction<{ jurisdiction: JurisdictionCode }>) => {
      state.jurisdiction = action.payload.jurisdiction;
      state.query = '';
      state.selectedKnowledgebaseIds = [];
      state.searchTerm = '';
      state.isSubmitting = false;
      // Reset filters and sorting
      state.relevanceFilter = [];
      state.contentTypeFilter = [];
      state.confidenceRange = [0, 1];
      state.knowledgebaseFilter = [];
      state.scoreThreshold = 0;
      state.sortBy = 'relevance';
      state.sortDirection = 'asc';
    },
    // Filter and sort actions
    setRelevanceFilter: (state, action: PayloadAction<string[]>) => {
      state.relevanceFilter = action.payload;
    },
    setContentTypeFilter: (state, action: PayloadAction<string[]>) => {
      state.contentTypeFilter = action.payload;
    },
    setConfidenceRange: (state, action: PayloadAction<[number, number]>) => {
      state.confidenceRange = action.payload;
    },
    setKnowledgebaseFilter: (state, action: PayloadAction<string[]>) => {
      state.knowledgebaseFilter = action.payload;
    },
    setScoreThreshold: (state, action: PayloadAction<number>) => {
      state.scoreThreshold = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },
    toggleSortDirection: (state) => {
      state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    resetFilters: (state) => {
      state.relevanceFilter = [];
      state.contentTypeFilter = [];
      state.confidenceRange = [0, 1];
      state.knowledgebaseFilter = [];
      state.scoreThreshold = 0;
    }
  }
});

export const {
  setJurisdiction,
  setQuery,
  setSelectedKnowledgebases,
  toggleKnowledgebase,
  selectAllKnowledgebases,
  selectNoKnowledgebases,
  setSearchTerm,
  setIsSubmitting,
  resetDocumentSearch,
  setRelevanceFilter,
  setContentTypeFilter,
  setConfidenceRange,
  setKnowledgebaseFilter,
  setScoreThreshold,
  setSortBy,
  setSortDirection,
  toggleSortDirection,
  resetFilters
} = documentSearchSlice.actions;

// Helper function to apply filters and sorting to search results or aggregated documents
// Works with both raw search results (chunks) and aggregated documents
export const getFilteredAndSortedResults = <T extends {
  relevance?: string;
  contentType?: string;
  confidence?: number;
  knowledgebaseId?: string;
  score?: number;
  documentTitle?: string;
  knowledgebaseName?: string;
  documentId?: string;
  reasoning?: string;
  keyTerms?: string[];
  knowledgebaseDomain?: string;
}>(
  searchResults: T[],
  filters: {
    relevanceFilter: string[];
    contentTypeFilter: string[];
    confidenceRange: [number, number];
    knowledgebaseFilter: string[];
    scoreThreshold: number;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
  }
): T[] => {
  if (!searchResults || searchResults.length === 0) {
    return [];
  }

  let results = [...searchResults];

  // Apply filters
  if (filters.relevanceFilter.length > 0) {
    results = results.filter(r => r.relevance && filters.relevanceFilter.includes(r.relevance));
  }

  if (filters.contentTypeFilter.length > 0) {
    results = results.filter(r => r.contentType && filters.contentTypeFilter.includes(r.contentType));
  }

  if (filters.confidenceRange[0] > 0 || filters.confidenceRange[1] < 1) {
    results = results.filter(
      r => r.confidence !== undefined && r.confidence >= filters.confidenceRange[0] && r.confidence <= filters.confidenceRange[1]
    );
  }

  if (filters.knowledgebaseFilter.length > 0) {
    results = results.filter(r => r.knowledgebaseId && filters.knowledgebaseFilter.includes(r.knowledgebaseId));
  }

  if (filters.scoreThreshold > 0) {
    results = results.filter(r => r.score !== undefined && r.score >= filters.scoreThreshold);
  }

  // Apply sorting
  results.sort((a, b) => {
    let comparison = 0;
    
    switch (filters.sortBy) {
      case 'relevance': {
        const relevanceOrder = ['highly_relevant', 'moderately_relevant', 'slightly_relevant', 'not_relevant'];
        const aIndex = a.relevance ? relevanceOrder.indexOf(a.relevance) : 999;
        const bIndex = b.relevance ? relevanceOrder.indexOf(b.relevance) : 999;
        comparison = aIndex - bIndex;
        break;
      }
      case 'confidence':
        comparison = (a.confidence || 0) - (b.confidence || 0);
        break;
      case 'score':
        comparison = (a.score || 0) - (b.score || 0);
        break;
      case 'documentTitle':
        comparison = (a.documentTitle || '').localeCompare(b.documentTitle || '');
        break;
      case 'knowledgebase':
        comparison = (a.knowledgebaseName || '').localeCompare(b.knowledgebaseName || '');
        break;
      default:
        comparison = 0;
    }

    return filters.sortDirection === 'asc' ? comparison : -comparison;
  });

  return results;
};

export default documentSearchSlice.reducer;

